import { useCallback, useMemo } from "react";

import { isEmptyArray } from "@/lib/chillnn/is-empty-array";
import type {
	AirHostPricePlan,
	SiteControllerSettingAirHostPricePLan_SettingPage_QueryQuery,
} from "@/lib/gql/__generated__/graphql";

import { QuestionCardItem } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-item";
import { QuestionCardItemsWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-items-wrapper";
import type { SiteControllerSettingAirHostFormModel } from "@/components/features/settings/site-controller/model/air-host/type";
import type { SiteControllerSettingAirHostFormModelMethodsType } from "@/components/features/settings/site-controller/model/air-host/use-case/use-create-model/methods";
import type { SiteControllerSettingBaseFormModel } from "@/components/features/settings/site-controller/model/base/type";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";
import { FormItem } from "@/components/ui/form/_base/form";
import { SelectFieldNew } from "@/components/ui/form/fields-new/select-field";

type Props = {
	airHostViewModel: SiteControllerSettingAirHostFormModel["ViewModel"];
	siteControllerSettingAirHostFormModelMethods: SiteControllerSettingAirHostFormModelMethodsType;
	queryData: SiteControllerSettingBaseFormModel["QueryData"];
	onRefetchAirHostPricePlan: (input: {
		hotelID: string;
		airHostPartnerID: string;
		airHostApiAccessToken: string;
	}) => Promise<SiteControllerSettingAirHostPricePLan_SettingPage_QueryQuery | undefined>;
	pricePlanData: AirHostPricePlan[];
	onSetPricePlanData: (pricePlanData: AirHostPricePlan[]) => void;
};

export const SettingsSiteControllerAirHostRoomMappingSection = ({
	airHostViewModel,
	queryData,
	onRefetchAirHostPricePlan,
	siteControllerSettingAirHostFormModelMethods,
	pricePlanData,
	onSetPricePlanData,
}: Props) => {
	const {
		onAppendRoomMapping,
		onRemoveRoomMapping,
		onChangeRoomMappingChillnnRoomID,
		onChangeRoomMappingAirHostPlanID,
	} = siteControllerSettingAirHostFormModelMethods;

	const handleFetchAirHostPricePlan = useCallback(async () => {
		const res = await onRefetchAirHostPricePlan({
			hotelID: airHostViewModel.hotelID,
			airHostPartnerID: airHostViewModel.airHostSettings.airHostPartnerID.value,
			airHostApiAccessToken: airHostViewModel.airHostSettings.airHostAPIAccessToken.value,
		});

		if (!res?.siteControllerSettingAirHostPricePlanDataFetch.airHostPricePlanData) {
			notifyUserEventInfo({
				variant: "error",
				title: "AirHostの料金プラン一覧を取得できませんでした",
			});
			onSetPricePlanData([]);
			return;
		}

		onSetPricePlanData(res?.siteControllerSettingAirHostPricePlanDataFetch.airHostPricePlanData ?? []);
		notifyUserEventInfo({
			title: "AirHostの料金プラン一覧を取得しました",
		});

		if (isEmptyArray(airHostViewModel.airHostSettings.chillnnRoomAndAirHostPricePlanMappings.value)) {
			// 紐付けがない場合は、空の料金プランを設定
			onAppendRoomMapping();
		}
	}, [onRefetchAirHostPricePlan, airHostViewModel, onSetPricePlanData, onAppendRoomMapping]);

	const canEditRoomMapping = useMemo(() => {
		return pricePlanData.length > 0 && queryData.roomKVList.length > 0;
	}, [pricePlanData, queryData.roomKVList.length]);

	/**
	 * CHILLNNの部屋の選択肢
	 */
	const chillnnRoomOptions = useMemo(() => {
		return queryData.roomKVList.map((room) => ({
			id: room.roomID,
			label: room.name,
			value: room.roomID,
		}));
	}, [queryData.roomKVList]);

	/**
	 * AirHostの料金プランの選択肢
	 */
	const airHostPricePlanDataOptions = useMemo(() => {
		return pricePlanData.map((airHostPricePlan) => ({
			id: airHostPricePlan.airHostPricePlanID,
			label: airHostPricePlan.airHostPricePlanName,
			value: airHostPricePlan.airHostPricePlanID,
		}));
	}, [pricePlanData]);

	/**
	 * 選択されたCHILLNNの部屋ID
	 */
	const selectedChillnnRoomIDs = useMemo(() => {
		return airHostViewModel.airHostSettings.chillnnRoomAndAirHostPricePlanMappings.value
			.map((mapping) => mapping.chillnnRoomID.value)
			.filter((id) => id !== "");
	}, [airHostViewModel.airHostSettings.chillnnRoomAndAirHostPricePlanMappings.value]);

	/**
	 * 選択されたAirHostの料金プランID
	 */
	const selectedAirHostPlanIDs = useMemo(() => {
		return airHostViewModel.airHostSettings.chillnnRoomAndAirHostPricePlanMappings.value
			.map((mapping) => mapping.airHostPlanID.value)
			.filter((id) => id !== "");
	}, [airHostViewModel.airHostSettings.chillnnRoomAndAirHostPricePlanMappings.value]);

	/**
	 * 選択されたCHILLNNの部屋IDを除外した選択肢
	 */
	const getFilteredChillnnRoomOptions = useCallback(
		(currentValue: string) => {
			return chillnnRoomOptions.filter(
				(option) => option.value === currentValue || !selectedChillnnRoomIDs.includes(option.value),
			);
		},
		[chillnnRoomOptions, selectedChillnnRoomIDs],
	);

	/**
	 * 選択されたAirHostの料金プランIDを除外した選択肢
	 */
	const getFilteredAirHostPricePlanOptions = useCallback(
		(currentValue: string) => {
			return airHostPricePlanDataOptions.filter(
				(option) => option.value === currentValue || !selectedAirHostPlanIDs.includes(option.value),
			);
		},
		[airHostPricePlanDataOptions, selectedAirHostPlanIDs],
	);

	const canFetchAirHostPricePlan = useMemo(() => {
		return !airHostViewModel.isDirty && !airHostViewModel.hasError;
	}, [airHostViewModel.isDirty, airHostViewModel.hasError]);

	return (
		<div className="space-y-12">
			<div>
				<Button onClick={handleFetchAirHostPricePlan} variant="outline" disabled={!canFetchAirHostPricePlan}>
					AirHostの料金プランを取得
				</Button>
			</div>

			{canEditRoomMapping && (
				<QuestionCardItemsWrapper appendItem={onAppendRoomMapping}>
					<FormItem>
						<div>
							<p className="font-semibold underline underline-offset-4">
								一度に保存できる紐付け設定は5件までです。
								<br />
								5件を超える場合は、複数回に分けて紐付け設定、保存してください。
							</p>
						</div>
						{airHostViewModel.airHostSettings.chillnnRoomAndAirHostPricePlanMappings.value.map(
							(mapping, index) => {
								const handleRemoveRoomMapping = () => {
									if (!window.confirm("この紐付けを削除しますか？")) {
										return;
									}

									onRemoveRoomMapping(index);
								};

								return (
									<QuestionCardItem
										key={`room_mapping_${index}`}
										removeItem={handleRemoveRoomMapping}
										className="w-full xl:w-1/2"
									>
										<div className="flex flex-col gap-y-10">
											<SelectFieldNew
												label="CHILLNNの部屋"
												value={mapping.chillnnRoomID.value}
												errorMessage={mapping.chillnnRoomID.error}
												className="w-2/3 2xl:w-1/2"
												options={getFilteredChillnnRoomOptions(mapping.chillnnRoomID.value)}
												placeholder="未選択"
												onValueChange={(value) =>
													onChangeRoomMappingChillnnRoomID(value, index)
												}
											/>
											<SelectFieldNew
												label="AirHostの料金プラン"
												value={mapping.airHostPlanID.value}
												errorMessage={mapping.airHostPlanID.error}
												className="w-2/3 2xl:w-1/2"
												options={getFilteredAirHostPricePlanOptions(
													mapping.airHostPlanID.value,
												)}
												onValueChange={(value) => {
													const targetPricePlan = pricePlanData.find(
														(item) => item.airHostPricePlanID === value,
													);

													if (!targetPricePlan) {
														return;
													}

													onChangeRoomMappingAirHostPlanID(
														value,
														targetPricePlan.airHostRoomTypeID,
														targetPricePlan.airHostRoomCheckInTime,
														targetPricePlan.airHostRoomCheckOutTime,
														index,
													);
												}}
												placeholder="未選択"
											/>
										</div>
									</QuestionCardItem>
								);
							},
						)}
					</FormItem>
				</QuestionCardItemsWrapper>
			)}
		</div>
	);
};

SettingsSiteControllerAirHostRoomMappingSection.displayName = "SettingsSiteControllerAirHostRoomMappingSection";
